/**
 * Firebase Analytics (GA4) — ATLAS reporting its own usage.
 *
 * ATLAS gets its **own GA4 property**, not a stream on an app's. That matters
 * beyond tidiness: once the property id is added to the backend's
 * `FIREBASE_ANALYTICS_PROPERTIES`, the existing Dev Dashboard metrics strip
 * renders ATLAS alongside the mobile apps with no new UI at all. The reader
 * half already exists — see `services/firebase-analytics.service.ts`. This is
 * only the writer.
 *
 * Two limits worth knowing before trusting a number here:
 *
 *  - GA4 keys on an anonymous client id, so it answers "how many sessions
 *    opened the eval form", never "did a specific person open it". The
 *    `userId` below is set as a GA4 *user property*, which helps segmentation
 *    but is still not a reliable per-person audit trail.
 *  - GA4 samples and thresholds low-volume reports. With ~30 users, expect
 *    some cards to withhold figures entirely rather than show a small number.
 *
 * Dormant unless the `VITE_FIREBASE_*` vars were set at build time. The whole
 * Firebase SDK is imported dynamically so an unconfigured build never pays the
 * bundle cost — this is the heaviest dependency ATLAS has, and the prod droplet
 * builds Vite on-box.
 */

/**
 * Firebase web config. Public by design — these identify the project, they do
 * not authorise anything, and Google documents them as safe to ship in client
 * code. Access is controlled by the property's own rules, not by hiding these.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined,
};

/**
 * The minimum that makes a GA4 write possible. `measurementId` is what routes
 * events to the property, and `appId`/`apiKey` are what the SDK refuses to
 * initialise without — a partial config is treated as "not configured" rather
 * than initialising something that will 400 on every event.
 */
const isConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.appId && firebaseConfig.measurementId,
);

type AnalyticsHandle = Awaited<ReturnType<typeof loadAnalytics>>;

async function loadAnalytics() {
  const [{ initializeApp }, { initializeAnalytics, isSupported }] = await Promise.all([
    import('@firebase/app'),
    import('@firebase/analytics'),
  ]);

  // Analytics is unsupported in some environments the app legitimately runs in
  // — notably any browser with cookies disabled. `isSupported` is the
  // documented guard; without it the SDK throws on those clients.
  if (!(await isSupported())) return null;

  // `send_page_view: false` is load-bearing, not a tidy-up. Left on, gtag fires
  // its own `page_view` the moment it initialises and fills `page_title` from
  // `document.title` — which in ATLAS is the *product name*, e.g.
  // "COLORado Panel 2 — ATLAS". That put unreleased fixture names into Google,
  // the one thing the file header says never to send. `trackPageView` below
  // sends every view explicitly, with a scrubbed route pattern instead.
  //
  // `initializeAnalytics` rather than `getAnalytics` because only the former
  // takes a config, and it must be the call that creates the instance — a
  // `getAnalytics` anywhere earlier wins and the setting is silently ignored.
  return initializeAnalytics(initializeApp(firebaseConfig), {
    config: { send_page_view: false },
  });
}

let handle: AnalyticsHandle | null = null;
let ready: Promise<AnalyticsHandle | null> | null = null;

/**
 * Begin loading analytics. Idempotent, and deliberately not awaited by callers
 * — nothing in the UI should block on a telemetry SDK.
 */
export function initAnalytics(): void {
  if (ready || !isConfigured) return;

  ready = loadAnalytics()
    .then((a) => {
      handle = a;
      return a;
    })
    .catch(() => {
      // A blocked or failed analytics load is not an application error and is
      // not reported: ad blockers make this a routine, expected outcome, and
      // routing it to Bugsnag would manufacture a large volume of noise.
      return null;
    });
}

/** Whether events are actually being sent. */
export const isAnalyticsEnabled = (): boolean => handle !== null;

/**
 * Record an event. Safe to call before `initAnalytics` has resolved — the call
 * queues behind the in-flight load rather than being dropped.
 *
 * Never pass free-text the user typed, a product name, or anything else
 * identifying. GA4 is a third party and this data leaves the tenant: send the
 * *shape* of what happened, never its content.
 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!ready) return;

  void ready.then(async (a) => {
    if (!a) return;
    try {
      const { logEvent } = await import('@firebase/analytics');
      logEvent(a, name, params);
    } catch {
      // Non-fatal by design — see initAnalytics.
    }
  });
}

/**
 * Reduce a concrete URL path to its route *pattern*, replacing every record id
 * with `:id` — `/products/PRD-123/pdlc/tasks/120987` becomes
 * `/products/:id/pdlc/tasks/:id`.
 *
 * Done by inspection rather than by asking the router: `main.tsx` mounts a
 * single catch-all `path: '*'` route and declares the real ones with `<Routes>`
 * inside it, so `useMatches()` reports only `*` and can't supply a pattern.
 *
 * The test is what an id looks like, not what a page is called, so a route
 * added later is scrubbed without anyone remembering to come back here. It errs
 * towards scrubbing: a static segment wrongly replaced costs one row of
 * reporting detail, a product code wrongly kept is an unreleased fixture name
 * sitting in Google.
 */
export function toRoutePattern(path: string): string {
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      // A Mongo ObjectId, and an Asana gid or any other bare number. Both are
      // otherwise indistinguishable from a route word by shape alone.
      if (/^[0-9a-f]{24}$/i.test(segment) || /^\d+$/.test(segment)) return ':id';
      // Every route segment ATLAS declares is kebab-case — `asset-tracking`,
      // `fixture-comparison`, `_demo-primitives`. Anything else is an id or an
      // opaque token: `PRD-123` and `USR-07k9wAlV` fail on the capitals, a
      // share/sign link fails on its random casing and digits.
      if (!/^_?[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(segment)) return ':id';
      // A token that happens to be all lowercase would have passed the test
      // above. No segment ATLAS names is this long without a hyphen — the
      // longest, `barcode-item-prefixes`, has two.
      if (segment.length >= 16 && !segment.includes('-')) return ':id';
      return segment;
    })
    .join('/');
}

/**
 * Record a route change.
 *
 * The path is scrubbed to its pattern before it is sent — see
 * `toRoutePattern`. Nothing free-text, and no record id, reaches GA4 from here.
 *
 * Takes the path and nothing else, deliberately. This used to accept an
 * optional `title` that was forwarded to GA4 unscrubbed, which is the exact
 * shape of the bug `toRoutePattern` exists to prevent: free text from a caller,
 * straight to a third party. Nothing ever passed it. Don't add it back — a
 * caller with something to say about the page should name a route, not a title.
 */
export function trackPageView(path: string): void {
  const pattern = toRoutePattern(path);
  trackEvent('page_view', {
    page_path: pattern,
    // Deliberately the pattern again, never `document.title`. gtag falls back
    // to `document.title` when `page_title` is absent, so this has to be sent
    // explicitly — omitting it reinstates the leak rather than avoiding it.
    page_title: pattern,
  });
}

/**
 * The actions worth reporting, matched against the API call that performs them.
 *
 * Instrumented here, at the one place every request already passes through,
 * rather than at a `trackEvent` call in each component. Same coverage, one file
 * instead of a dozen, and a feature added later cannot half-ship its analytics
 * — it either appears in this table or it is deliberately absent.
 *
 * Read this as the answer to "what are people actually doing in ATLAS", which
 * page views cannot answer: before this existed GA4 held `page_view` and
 * `first_visit` and nothing else.
 *
 * Only successful mutations reach here, so a count is work completed, not work
 * attempted. Keep the list short and business-meaningful: a preference toggle
 * or an admin settings write is noise, and every event is a row someone has to
 * read later.
 */
const TRACKED_ACTIONS: ReadonlyArray<readonly [method: string, pattern: RegExp, event: string]> = [
  ['POST', /^\/submissions$/, 'evaluation_created'],
  ['POST', /^\/submissions\/[^/]+\/submit$/, 'evaluation_submitted'],
  ['POST', /^\/products$/, 'product_created'],
  // `[^/]+$` so this is the product itself, not one of the many sub-resources
  // hanging off it — a SharePoint link or an Asana field write is not an edit
  // of the product in the sense anyone reading the report will mean.
  ['PUT', /^\/products\/[^/]+$/, 'product_updated'],
  ['PATCH', /^\/products\/[^/]+$/, 'product_updated'],
  ['POST', /^\/products\/[^/]+\/spec\/save$/, 'spec_saved'],
  ['POST', /^\/products\/[^/]+\/spec\/publish$/, 'spec_published'],
  ['POST', /^\/products\/[^/]+\/spec\/review$/, 'spec_review_requested'],
  ['POST', /^\/products\/[^/]+\/spec\/review\/[^/]+\/approve$/, 'spec_review_approved'],
  ['POST', /^\/products\/[^/]+\/spec\/review\/[^/]+\/reject$/, 'spec_review_rejected'],
  ['POST', /^\/form-requests$/, 'form_request_sent'],
  ['POST', /^\/approvals\/[^/]+\/approve$/, 'form_approved'],
  ['POST', /^\/bulletins$/, 'bulletin_created'],
  ['POST', /^\/physical-assets$/, 'asset_registered'],
  ['POST', /^\/feedback\/report-issue$/, 'bug_reported'],
];

/**
 * Report a completed API call as a named action, when it is one of the few the
 * table above names. Called from `services/api.ts` on success only.
 *
 * The event name is the whole payload: no ids, no product names, nothing the
 * user typed. GA4 answers "how many evaluations were submitted this week", and
 * the login audit and the database answer who and which — first-party, where
 * that detail belongs.
 */
export function trackApiAction(method: string, endpoint: string): void {
  const event = matchApiAction(method, endpoint);
  if (event) trackEvent(event);
}

/**
 * The matching half of `trackApiAction`, split out because it is the part with
 * a wrong answer available — a pattern that also catches a sub-resource, or a
 * preference write counted as real work. Pure, so the test asserts on a return
 * value rather than trying to spy on a module-internal call.
 */
export function matchApiAction(method: string, endpoint: string): string | undefined {
  if (method === 'GET') return undefined;

  const path = endpoint.split('?')[0];
  for (const [trackedMethod, pattern, event] of TRACKED_ACTIONS) {
    if (method === trackedMethod && pattern.test(path)) return event;
  }
  return undefined;
}

/**
 * Attach the signed-in user as a GA4 user property.
 *
 * The ATLAS `USR-` id, never an email — an email in GA4 would be personal data
 * in a third-party analytics product, which is both a privacy problem and
 * against Google's terms. The id is opaque outside ATLAS.
 */
export function setAnalyticsUser(userId?: string): void {
  if (!ready) return;

  void ready.then(async (a) => {
    if (!a) return;
    try {
      const { setUserId } = await import('@firebase/analytics');
      setUserId(a, userId ?? null);
    } catch {
      // Non-fatal by design — see initAnalytics.
    }
  });
}
